import { useMemo } from "react";
import { supabase } from "../lib/supabase";
import type { AdapterPublishRow } from "../lib/supabase";
import { useSupabaseQuery } from "../hooks/useSupabaseQuery";
import { fmtRelative, fmtInt } from "../lib/format";

export default function Adapters() {
  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }, []);

  const { data, error, loading } = useSupabaseQuery<AdapterPublishRow[]>(
    async () =>
      supabase
        .from("adapter_publishes")
        .select("*")
        .gte("published_at", since)
        .order("published_at", { ascending: false })
        .limit(5000) as unknown as Promise<any>,
    [since],
    60_000,
  );

  const byAdapter = useMemo(() => {
    const rows = data || [];
    const map = new Map<string, {
      adapter: string;
      total: number;
      byStatus: Record<string, number>;
      latest: AdapterPublishRow | null;
    }>();
    for (const r of rows) {
      const a = r.adapter || "unknown";
      let entry = map.get(a);
      if (!entry) {
        entry = { adapter: a, total: 0, byStatus: {}, latest: null };
        map.set(a, entry);
      }
      entry.total++;
      const st = r.status || "unknown";
      entry.byStatus[st] = (entry.byStatus[st] || 0) + 1;
      if (!entry.latest || (r.published_at || "") > (entry.latest.published_at || "")) {
        entry.latest = r;
      }
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Adapters active (7d)" value={fmtInt(byAdapter.length)} />
        <Stat label="Publishes (7d)" value={fmtInt(byAdapter.reduce((s, a) => s + a.total, 0))} />
        <Stat
          label="Latest publish"
          value={byAdapter[0]?.latest ? fmtRelative(byAdapter[0].latest.published_at) : "—"}
          sub={byAdapter[0]?.adapter}
        />
      </div>

      <div className="card">
        <h3 className="text-sm font-medium mb-3">Per-adapter status (7d)</h3>
        {loading && !data && <p className="text-sm text-chrome-400">Loading…</p>}
        {error && <p className="text-sm text-bad">{(error as Error).message}</p>}
        {data && byAdapter.length === 0 && (
          <p className="text-sm text-chrome-400">No adapter publishes in the last 7 days.</p>
        )}
        <div className="space-y-3">
          {byAdapter.map((a) => (
            <div key={a.adapter} className="border-t border-ink-800 pt-3 first:border-t-0 first:pt-0">
              <div className="flex items-baseline justify-between gap-4">
                <h4 className="font-mono text-sm text-chrome-100">{a.adapter}</h4>
                <span className="text-xs text-chrome-400">
                  latest {fmtRelative(a.latest?.published_at)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(a.byStatus)
                  .sort((x, y) => y[1] - x[1])
                  .map(([status, n]) => (
                    <span
                      key={status}
                      className={`chip ${
                        status === "scheduled" || status === "published"
                          ? "chip-approved"
                          : status === "failed" || status === "error"
                          ? "chip-rejected"
                          : "chip-pending"
                      }`}
                    >
                      {status}: {fmtInt(n)}
                    </span>
                  ))}
                <span className="chip chip-pending ml-auto">total: {fmtInt(a.total)}</span>
              </div>
              {a.latest?.private_url && (
                <a
                  href={a.latest.private_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono text-lane2 hover:underline mt-2 inline-block truncate max-w-full"
                >
                  {a.latest.private_url}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-xs text-chrome-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-semibold mt-1 font-mono">{value}</p>
      {sub && <p className="text-xs text-chrome-400 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}
